import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import '../components/ReleaseNotes.css';

interface Release {
  version: string;
  date: string;
  title: string;
  description: string;
  features: string[];
  improvements: string[];
  fixes: string[];
}

const releases: Release[] = [
  {
    version: 'v1.6.0',
    date: '2026-01-25',
    title: 'Precisão de Métricas e Performance',
    description: 'Melhorias na precisão das métricas de deployment e otimizações de performance para garantir dados mais confiáveis e respostas mais rápidas.',
    features: [],
    improvements: [
      'Melhoria na precisão das métricas de deployment com filtragem automática de builds',
      'Otimização de consultas ao banco de dados para melhor performance da API',
      'Melhoria na conexão entre deployments e workflows para dados mais confiáveis',
      'Correção na contagem de deployments para garantir precisão nas métricas'
    ],
    fixes: [
      'Correção de contagem incorreta de deployments que incluía builds',
      'Correção de duplicação de deployments nas métricas'
    ]
  },
  {
    version: 'v1.5.0',
    date: '2026-01-23',
    title: 'Otimização de Performance',
    description: 'Melhorias significativas de performance em todas as páginas com otimizações de carregamento e renderização.',
    features: [],
    improvements: [
      'Redução de 30-50% no tempo de carregamento das páginas principais',
      'Otimização de carregamento de dados com processamento paralelo',
      'Melhorias na busca e filtros para reduzir requisições desnecessárias',
      'Otimização de renderização de gráficos e tabelas',
      'Melhorias na sincronização de deployments',
      'Atualização automática de status de deployments'
    ],
    fixes: [
      'Correção de problemas de carregamento em algumas páginas',
      'Correção de duplicação de dados em algumas visualizações'
    ]
  },
  {
    version: 'v1.4.0',
    date: '2026-01-23',
    title: 'Sistema de Validação e Notificações',
    description: 'Implementação de validação automática de Pull Requests e sistema completo de notificações para manter a qualidade dos dados.',
    features: [
      'Validação automática de PRs com verificação de integridade de datas',
      'Sistema de notificações em tempo real para problemas de validação',
      'Página "Minhas Atividades" para visualizar PRs do próprio autor',
      'Redirecionamento inteligente nas notificações (autor vs. proprietário)',
      'Scripts de validação e normalização de datas (validate-dates.sh)',
      'Script para buscar dados brutos do GitHub (fetch-pr-github.sh)'
    ],
    improvements: [
      'Correção completa de normalização de timezone em todas as datas',
      'Melhoria na validação de firstCommitDate usando dados diretos do GitHub',
      'Paginação na página de notificações',
      'Renomeação de "Contribuição" para "Meu dashboard" no menu'
    ],
    fixes: [
      'Correção de timezone em firstCommitDate que estava sendo convertido incorretamente',
      'Ajuste na validação de datas para buscar commits diretamente do GitHub',
      'Correção de normalização de datas na leitura do Firestore'
    ]
  },
  {
    version: 'v1.3.0',
    date: '2026-01-08',
    title: 'Melhorias em Métricas e Sincronização',
    description: 'Enriquecimento de dados, novas métricas de contribuição e melhorias significativas na sincronização manual.',
    features: [
      'Enriquecimento automático de Pull Requests com commits e dados atualizados',
      'Métrica de PRs Revisados para análise de contribuição',
      'Gráfico de distribuição de revisores',
      'Substituição de "Deploys por Dia" por "Deploy Frequency no Período"',
      'Sistema de abas e paginação na página de configurações'
    ],
    improvements: [
      'Melhorias na sincronização manual com tratamento de timezone',
      'Desduplicação de deployments e workflows nas métricas',
      'Atualização automática de PRs quando estado muda',
      'Melhorias na UX da paginação de repositórios',
      'Exibição de contadores de dados sincronizados'
    ],
    fixes: [
      'Correção de cálculos de métricas: Cycle Time, Lead Time, Merge PR Rate',
      'Correção de cálculos de métricas de incidentes usando StartedAt/DetectedAt',
      'Correção de agregação de PRsReviewed',
      'Correção de exibição de syncDate usando UTC',
      'Correção de problemas de timezone na sincronização manual'
    ]
  },
  {
    version: 'v1.2.0',
    date: '2026-01-20',
    title: 'Sistema de Permissões e Gestão',
    description: 'Implementação de sistema completo de permissões customizáveis, gestão de membros e controle de acesso granular.',
    features: [
      'Sistema de permissões customizáveis por organização',
      'Gestão completa de membros com convites',
      'Controle de acesso baseado em permissões no frontend',
      'Sistema de postmortem para análise de incidentes',
      'Gestão de incidentes com timeline e impactos',
      'Restrição de associação GitHub a administradores'
    ],
    improvements: [
      'Melhorias no postmortem e gestão de membros',
      'Reorganização de menu de configurações',
      'Melhorias na UX do dashboard com seções organizadas',
      'Ajustes de UI: glassmorphism, cores do tema escuro',
      'Refatoração da página de contribuidores'
    ],
    fixes: [
      'Correção de exibição de Merge PR Rate no frontend',
      'Correção de contraste do badge de notificação',
      'Correção de erros de lint para build:ci'
    ]
  },
  {
    version: 'v1.1.0',
    date: '2025-12-26',
    title: 'Infraestrutura e Integração',
    description: 'Migração para arquitetura moderna com RabbitMQ, otimizações de webhooks e sistema de configuração flexível.',
    features: [
      'Migração de Hangfire para RabbitMQ com projetos separados (API e Consumer)',
      'Sistema de configuração de extração de dados flexível',
      'Processamento de workflow_job para criar deployments automaticamente',
      'Coleta de workflow jobs no sync manual',
      'Otimização de criação e processamento de webhooks do GitHub',
      'Implementação de rate limiting para GitHub API'
    ],
    improvements: [
      'Refatoração do GitHubClient com factory e cache scoped',
      'Otimização de SelectRepositories para melhor performance',
      'Suporte para OrganizationId via header X-Organization-Id',
      'Validação real de credenciais GitHub OAuth',
      'Otimização de performance do backend com paralelização e cache',
      'Tratamento de rate limit do GitHub no frontend'
    ],
    fixes: [
      'Correção de criação de webhook e permissão de webhooks do GitHub no middleware',
      'Correção de validação de credenciais OAuth do GitHub',
      'Correção de GetOrganizationByUserAsync para usar User.OrganizationId',
      'Correção de processamento de pull_request e validação de WorkflowRun'
    ]
  },
  {
    version: 'v1.0.0',
    date: '2025-12-17',
    title: 'Release Inicial',
    description: 'Lançamento inicial do Opsight com sistema completo de coleta de métricas do GitHub, dashboard interativo e integração completa.',
    features: [
      'Sistema completo de coleta de dados do GitHub (PRs, Deployments, Workflow Runs)',
      'Dashboard interativo com métricas de engenharia',
      'Sistema de autenticação JWT customizado',
      'Gestão de organizações e repositórios',
      'Integração completa com GitHub via OAuth',
      'Wizard de configuração do GitHub OAuth',
      'Interface de coleta manual de dados',
      'Página de repositórios com filtros de data',
      'Upload de foto de perfil via Firebase Storage',
      'Sistema de notificações básico',
      'Dark mode como padrão com background animado'
    ],
    improvements: [
      'Melhorias na UX da seleção de repositórios',
      'Melhorias na experiência de notificações',
      'Melhorias na visualização de loading',
      'Cache busting e versão dinâmica progressiva'
    ],
    fixes: [
      'Correção de fluxo de registro e cadastro',
      'Correção de tratamento de erros do Firebase',
      'Correção de refresh infinito no Dashboard',
      'Correção de erros de linting para build no CI'
    ]
  }
];

export const ReleaseNotes: React.FC = () => {
  return (
    <div className="landing-page">
      <Header />
      <main>
        <section className="release-notes-hero">
          <div className="container">
            <h1 className="release-notes-title">Release Notes</h1>
            <p className="release-notes-subtitle">
              Acompanhe todas as melhorias, novas funcionalidades e correções do Opsight
            </p>
          </div>
        </section>

        <section className="release-notes-content">
          <div className="container">
            {releases.map((release, index) => (
              <div key={release.version} className="release-card">
                <div className="release-header">
                  <div className="release-version-badge">{release.version}</div>
                  <div className="release-date">{new Date(release.date).toLocaleDateString('pt-BR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</div>
                </div>
                <h2 className="release-title">{release.title}</h2>
                <p className="release-description">{release.description}</p>

                {release.features.length > 0 && (
                  <div className="release-section">
                    <h3 className="release-section-title">
                      <span className="release-badge release-badge-feature">Novo</span>
                      Funcionalidades
                    </h3>
                    <ul className="release-list">
                      {release.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {release.improvements.length > 0 && (
                  <div className="release-section">
                    <h3 className="release-section-title">
                      <span className="release-badge release-badge-improvement">Melhoria</span>
                      Melhorias
                    </h3>
                    <ul className="release-list">
                      {release.improvements.map((improvement, idx) => (
                        <li key={idx}>{improvement}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {release.fixes.length > 0 && (
                  <div className="release-section">
                    <h3 className="release-section-title">
                      <span className="release-badge release-badge-fix">Correção</span>
                      Correções
                    </h3>
                    <ul className="release-list">
                      {release.fixes.map((fix, idx) => (
                        <li key={idx}>{fix}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
